import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts'
import { currentLocale } from '../../i18n'

// One colour per person, in a stable order (the row order), so a person keeps
// their colour while the plan is edited. Cycles past ten people.
const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4',
  '#7c5cd6', '#0f9fb5', '#a3a82c', '#c2415b', '#6b7280']

const monthLabel = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' })

/**
 * A project's planned hours per month, stacked by person -- the same bars as
 * the Disponibilidad chart, but for ONE project. Reads the staffing payload
 * (project_staffing.build_staffing): rows[].months = [{year, month, hours}].
 */
export default function ProjectMonthlyChart({ staffing }) {
  const { t } = useTranslation('clevel')
  const rows = staffing?.rows

  const { data, people } = useMemo(() => {
    const list = Array.isArray(rows) ? rows : []
    const people = list.filter(r => (r.total_hours || 0) > 0)
    const months = list[0]?.months || []
    const data = months.map((m, i) => {
      const point = { label: monthLabel(m.year, m.month) }
      for (const r of people) point[`p${r.id}`] = r.months?.[i]?.hours || 0
      return point
    })
    return { data, people }
  }, [rows])

  if (!people.length) {
    return <p className="text-sm text-gray-400 py-6 text-center">{t('scenarios.panel.chartEmpty')}</p>
  }

  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#eceef1" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} />
          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} unit=" h" width={52} />
          <Tooltip formatter={(v, name) => [`${Number(v).toFixed(1)} h`, name]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {people.map((r, i) => (
            <Bar key={r.id} dataKey={`p${r.id}`} name={r.name} stackId="h" fill={PALETTE[i % PALETTE.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
